import { FieldConfig } from '../../../services/ValidationService';

// Utility per le conversioni CBR/Ev2
export const ConversionUtils = {
    ev2ToCbr: (ev2Value: number): number => {
        if (ev2Value <= 50) {
            return ev2Value / 10;
        } else {
            return Math.pow(ev2Value / 17.6, 1 / 0.64);
        }
    },

    cbrToEv2: (cbrValue: number): number => {
        if (cbrValue <= 5) {
            return cbrValue * 10;
        } else {
            return 17.6 * Math.pow(cbrValue, 0.64);
        }
    },

    roundToDecimal: (value: number, decimals = 1): number => {
        return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
    },
};

// Hook per gestire le conversioni automatiche
export const useFieldConversions = (groupName: string, setFieldValue: (field: string, value: unknown) => void) => {
    const convertAndSetValues = (newToggleValue: string, currentValues: Record<string, unknown>) => {
        setTimeout(() => {
            // Determine the field naming pattern based on what fields exist
            const hasBaseCourseFields = currentValues.hasOwnProperty('cbrBaseCourse') || currentValues.hasOwnProperty('ev2BaseCourse');
            const hasDeformationModulus = currentValues.hasOwnProperty('deformationModulus');

            if (hasBaseCourseFields) {
                // UnpavedGiroud pattern - handles both base course and subgrade
                if (newToggleValue === 'cbr') {
                    // Convert from Ev2 to CBR
                    const ev2Base = currentValues.ev2BaseCourse || 100.0;
                    const ev2Sub = currentValues.ev2Subgrade || 40.0;

                    const convertedCbrBase = ConversionUtils.ev2ToCbr(Number(ev2Base));
                    const convertedCbrSub = ConversionUtils.ev2ToCbr(Number(ev2Sub));

                    setFieldValue(`${groupName}.cbrBaseCourse`, ConversionUtils.roundToDecimal(convertedCbrBase));
                    setFieldValue(`${groupName}.cbrSubgrade`, ConversionUtils.roundToDecimal(convertedCbrSub));

                    // Clear the modulus fields to prevent validation errors
                    setFieldValue(`${groupName}.ev2BaseCourse`, undefined);
                    setFieldValue(`${groupName}.ev2Subgrade`, undefined);
                } else if (newToggleValue === 'modulus') {
                    // Convert from CBR to Ev2
                    const cbrBase = currentValues.cbrBaseCourse || 15.0;
                    const cbrSub = currentValues.cbrSubgrade || 4.0;

                    const convertedEv2Base = ConversionUtils.cbrToEv2(Number(cbrBase));
                    const convertedEv2Sub = ConversionUtils.cbrToEv2(Number(cbrSub));

                    setFieldValue(`${groupName}.ev2BaseCourse`, ConversionUtils.roundToDecimal(convertedEv2Base));
                    setFieldValue(`${groupName}.ev2Subgrade`, ConversionUtils.roundToDecimal(convertedEv2Sub));

                    // Clear the CBR fields to prevent validation errors
                    setFieldValue(`${groupName}.cbrBaseCourse`, undefined);
                    setFieldValue(`${groupName}.cbrSubgrade`, undefined);
                }
            } else if (hasDeformationModulus) {
                // ContainerYard/ParkingDeck pattern - handles only subgrade parameters
                if (newToggleValue === 'cbr') {
                    // Convert from deformation modulus to CBR
                    const defMod = currentValues.deformationModulus || 30.0;
                    const convertedCbr = ConversionUtils.ev2ToCbr(Number(defMod));
                    setFieldValue(`${groupName}.cbrSubgrade`, ConversionUtils.roundToDecimal(convertedCbr));

                    // Clear the modulus field to prevent validation errors
                    setFieldValue(`${groupName}.deformationModulus`, undefined);
                } else if (newToggleValue === 'modulus') {
                    // Convert from CBR to deformation modulus
                    const cbrSub = currentValues.cbrSubgrade || 3.0;
                    const convertedMod = ConversionUtils.cbrToEv2(Number(cbrSub));
                    setFieldValue(`${groupName}.deformationModulus`, ConversionUtils.roundToDecimal(convertedMod));

                    // Clear the CBR field to prevent validation errors
                    setFieldValue(`${groupName}.cbrSubgrade`, undefined);
                }
            }
        }, 0);
    };

    return { convertAndSetValues };
};

// Hook per la logica di filtro condizionale
export const useConditionalFields = (dynamicFields: FieldConfig[], groupName: string, values: Record<string, unknown>) => {
    const getControllerValue = (fieldConfig: FieldConfig, key: 'showWhen' | 'hideWhen') => {
        const cond = fieldConfig[key];
        if (!cond) return undefined;
        // Prefer same group
        if (values?.[groupName] && typeof values[groupName] === 'object' && values[groupName] !== null) {
            const groupValues = values[groupName] as Record<string, unknown>;
            if (groupValues[cond.field] !== undefined) {
                return groupValues[cond.field];
            }
        }
        // If not found in same group, search other groups
        for (const [, groupValues] of Object.entries(values || {})) {
            if (groupValues && typeof groupValues === 'object' && cond.field in groupValues) {
                const typedGroupValues = groupValues as Record<string, unknown>;
                return typedGroupValues[cond.field];
            }
        }
        return undefined;
    };

    const getConditionallyFilteredFields = () => {
        return dynamicFields.filter(fieldConfig => {
            // 1. hideWhen takes precedence
            if (fieldConfig.hideWhen) {
                const hideValue = getControllerValue(fieldConfig, 'hideWhen');
                if (hideValue === fieldConfig.hideWhen.value) {
                    return false; // Hide field
                }
            }
            // 2. showWhen logic (if present)
            if (fieldConfig.showWhen) {
                const showValue = getControllerValue(fieldConfig, 'showWhen');
                return showValue === fieldConfig.showWhen.value;
            }
            // 3. No conditions: always show
            return true;
        });
    };

    return { getConditionallyFilteredFields };
};
